import { Link, useLocation, useNavigate } from "react-router-dom";
import { useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import { IoSearchSharp } from "react-icons/io5";
import { LiaBarsSolid } from "react-icons/lia";
import { RxCross2 } from "react-icons/rx";
import { openSignIn, openSignUp } from "../../features/modals/modalSlice";

// Components
import LinkNavbar from "../linksnavbar/LinkNavbar";
import SearchBar from "./SearchBar";
import AuthModals from "./AuthModals";
import ProfileDropdown from "./ProfileDropdown";
import TrendingReviews from "./TrendingReviews";

// Hooks
import { useSearch } from "../../hooks/useSearch";
import { useAuth } from "../../hooks/useAuth";
import { useReviews } from "../../hooks/useReviews";
import { useMobileNavigation } from "../../hooks/useMobileNavigation";

// Assets
import logoImg from "../../assets/images/logo.png";
import heroImg from "../../assets/images/heroimg.png";
import "./Navbar.css";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Use custom hooks
  const search = useSearch();
  const auth = useAuth();
  const reviews = useReviews();
  const mobile = useMobileNavigation();

  // Refs for search functionality
  const searchRef = useRef(null);
  const firstSearchRef = useRef(null);
  const profileDropdownRef = useRef(null);

  const dropdownRef = useRef(null);

  // Handle outside click for search suggestions
  useEffect(() => {
    const handleOutsideClick = (e) => {
      // console.log("event clicked", searchRef.current, e.target);
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        // Check if the click is on a suggestion item or "View More" button
        const isSuggestionClick =
          e.target.closest(".suggestion-box") ||
          e.target.closest(".suggestion-item") ||
          e.target.textContent === "View More";

        if (!isSuggestionClick) {
          setTimeout(() => {
            search.clearSuggestions();
          }, 100);
        }
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear suggestions when location changes
  useEffect(() => {
    search.clearSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutsideProfile = (event) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        auth.setProfileDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideProfile);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideProfile);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let timeoutId;
    const handleClickOutside = (event) => {
      timeoutId = setTimeout(() => {
        search.setIsDropdownOpen(false);
      }, 100);
    };

    if (search.isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.isDropdownOpen, search.setIsDropdownOpen]);

  // Handle search input changes
  const handleSearch = (e) => {
    search.setSearchTerm(e.target.value);
  };

  // Handle search request
  const handleSearchRequest = () => {
    if (search.searchTerm.trim()) {
      search.fetchSuggestions(search.searchTerm);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (item) => {
    if (item.id === "clear") {
      search.clearSuggestions();
      return;
    }

    const queryParams = new URLSearchParams({
      name: item.name,
      id: item.id,
    }).toString();

    if(search.searchType === "managers") {
      navigate(`/manager?${queryParams}`);
    } else {
      navigate(`/company-profile?${queryParams}`);
    }

    search.clearSuggestions();
  };

  // Handle focus for second search click
  const handleSecondSearchClick = () => {
    if (firstSearchRef.current) {
      firstSearchRef.current.focus();
    }
  };

  return (
    <div className="">
      <nav
        className="navbar navbar-expand-lg navbar-dark py-lg-0 px-lg-2 px-md-2 px-2"
        style={{ background: "#00796B" }}
      >
        <div className="container ">
          <a
            className="navbar-brand pe-0 py-lg-3 pb-md-0 pb-0 d-none d-lg-block"
            href="/"
          >
            <img
              src={logoImg}
              alt="Logo"
              style={{ width: "72px", marginBottom: "-2px" }}
            />
          </a>

          {/* Small Screen Navbar */}
          {location.pathname === "/" ? (
            <div className="width_sm_scrreens py-lg-0 py-md-2 py-2 d-lg-none">
              <div
                className="d-flex align-items-center w-100"
                style={{ gap: "8px" }}
              >
                <SearchBar
                  searchType={search.searchType}
                  searchTerm={search.searchTerm}
                  onSearchChange={handleSearch}
                  onSearchRequest={handleSearchRequest}
                  suggestions={search.suggestions}
                  onSuggestionClick={handleSuggestionClick}
                  onViewMore={search.handleViewMore}
                  lastKey={search.lastKey}
                  remainingCompanies={search.remainingCompanies}
                  isDropdownOpen={search.isDropdownOpen}
                  onDropdownToggle={() =>
                    search.setIsDropdownOpen(!search.isDropdownOpen)
                  }
                  onDropdownChange={search.handleDropdownChange}
                  dropdownVisible={false}
                  showDropdown={true}
                  isSmallScreen={true}
                  className="flex-grow-1"
                  ref={searchRef}
                />
                <div className="flex-shrink-0 position-relative">
                  <button
                    className="navbar-toggler p-0"
                    onClick={mobile.toggledrop}
                    style={{
                      background: "transparent",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "40px", // Match search bar height
                      width: "40px",
                    }}
                  >
                    <div
                      style={{
                        transition: "transform 0.3s ease",
                        transform: mobile.opensmdrop
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      }}
                    >
                      {mobile.opensmdrop ? (
                        <RxCross2 size={24} color="#ffffff" />
                      ) : (
                        <LiaBarsSolid size={24} color="#ffffff" />
                      )}
                    </div>
                  </button>
                  {mobile.opensmdrop && (
                    <div className="navbar_li_bg" ref={mobile.dropdownRef}>
                      <ul className="ps-0 mb-0">
                        {auth.isLoggedIn ? (
                          <>
                            <li
                              onClick={() => {
                                mobile.handleClosedrop();
                                navigate("/preference?activeTab=profile");
                              }}
                            >
                              Profile
                            </li>
                            <li
                              onClick={() => {
                                mobile.handleClosedrop();
                                navigate("/preference?activeTab=rated");
                              }}
                            >
                              Rated Managers
                            </li>
                            <li
                              onClick={() => {
                                mobile.handleClosedrop();
                                navigate("/preference?activeTab=saved");
                              }}
                            >
                              Saved Managers
                            </li>
                            <li className="contact_li_bg">
                              <Link
                                to="/contact-us"
                                className="contact-link d-block w-100"
                                style={{ color: "black" }}
                                onClick={mobile.handleClosedrop}
                              >
                                Contact Us
                              </Link>
                            </li>

                            {/* Buy Me a Coffee (Small Screen) */}
                            <li className="text-center mb-2">
                              <a
                                href="https://buymeacoffee.com/ratemymanager"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="d-inline-block"
                              >
                                <img
                                  src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                                  alt="Buy Me a Coffee"
                                  style={{ height: "28px", width: "auto" }}
                                />
                              </a>
                            </li>

                            <li className="logout-item" onClick={auth.handleSignOut}>Logout</li>
                            {/*<li onClick={auth.handleSignOut}>Logout</li>*/}
                          </>
                        ) : (
                          <>
                            <li
                              onClick={() => {
                                mobile.handleClosedrop();
                                dispatch(openSignUp());
                              }}
                            >
                              Sign Up
                            </li>
                            <li
                              onClick={() => {
                                mobile.handleClosedrop();
                                dispatch(openSignIn());
                              }}
                            >
                              Sign In
                            </li>

                            {/* Buy Me a Coffee (Small Screen) */}
                            <li className="text-center mb-2">
                              <a
                                href="https://buymeacoffee.com/ratemymanager"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="d-inline-block"
                              >
                                <img
                                  src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                                  alt="Buy Me a Coffee"
                                  style={{ height: "28px", width: "auto" }}
                                />
                              </a>
                            </li>

                          </>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="width_sm_scrreens py-lg-0 py-md-2 py-2 d-lg-none">
              <div
                className="d-flex justify-content-between align-items-center w-100"
                style={{ gap: "8px" }}
              >
                <div className="flex-shrink-0 position-relative">
                  <button
                    className="navbar-toggler p-0"
                    type="button"
                    onClick={mobile.toggledrop}
                    style={{
                      background: "transparent",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "40px",
                      width: "40px",
                    }}
                  >
                    <div
                      style={{
                        transition: "transform 0.3s ease",
                        transform: mobile.opensmdrop
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      }}
                    >
                      {mobile.opensmdrop ? (
                        <RxCross2 size={24} color="#ffffff" />
                      ) : (
                        <LiaBarsSolid size={24} color="#ffffff" />
                      )}
                    </div>
                  </button>
                  {mobile.opensmdrop && (
                    <div
                      className="navbar_li_bg_otherscreens"
                      ref={mobile.dropdownRef}
                    >
                      <ul className="ps-0 mb-0">
                        {auth.isLoggedIn ? (
                          <>
                            <li
                              onClick={() => {
                                mobile.handleClosedrop();
                                navigate("/preference?activeTab=profile");
                              }}
                            >
                              Profile
                            </li>
                            <li
                              onClick={() => {
                                mobile.handleClosedrop();
                                navigate("/preference?activeTab=rated");
                              }}
                            >
                              Rated Managers
                            </li>
                            <li
                              onClick={() => {
                                mobile.handleClosedrop();
                                navigate("/preference?activeTab=saved");
                              }}
                            >
                              Saved Managers
                            </li>
                            <li className="contact_li_bg">
                              <Link
                                to="/contact-us"
                                className="contact-link d-block w-100"
                                style={{ color: "black" }}
                                onClick={mobile.handleClosedrop}
                              >
                                Contact Us
                              </Link>
                            </li>

                            {/* Buy Me a Coffee (Small Screen) */}
                            <li className="text-center mb-2">
                              <a
                                href="https://buymeacoffee.com/ratemymanager"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="d-inline-block"
                              >
                                <img
                                  src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                                  alt="Buy Me a Coffee"
                                  style={{ height: "28px", width: "auto" }}
                                />
                              </a>
                            </li>

                            <li className="logout-item" onClick={auth.handleSignOut}>Logout</li>
                          </>
                        ) : (
                          <>
                            <li onClick={() => dispatch(openSignUp())}>
                              Sign Up
                            </li>
                            <li onClick={() => dispatch(openSignIn())}>
                              Sign In
                            </li>

                            {/* Buy Me a Coffee (Small Screen) */}
                            <li className="text-center mb-2">
                              <a
                                href="https://buymeacoffee.com/ratemymanager"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="d-inline-block"
                              >
                                <img
                                  src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                                  alt="Buy Me a Coffee"
                                  style={{ height: "28px", width: "auto" }}
                                />
                              </a>
                            </li>

                          </>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="flex-grow-1 d-flex justify-content-center">
                  <Link
                    className="navbar-brand pe-0 pb-lg-0 pb-md-0 pb-0 d-block d-lg-none"
                    to="/"
                  >
                    <img
                      src={logoImg}
                      alt="Logo"
                      style={{ width: "72px", marginBottom: "-11px" }}
                    />
                  </Link>
                </div>
                <div className="flex-shrink-0 position-relative">
                  <button
                    className="navbar-toggler p-0"
                    type="button"
                    onClick={mobile.toggledropsearch}
                    style={{
                      background: "transparent",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "40px",
                      width: "40px",
                    }}
                  >
                    <IoSearchSharp size={20} color="#ffffff" />
                  </button>
                  {mobile.opensmsearch && (
                    <div
                      className="navbar_search_bg_otherscreens w-100"
                      ref={mobile.dropdownRef}
                    >
                      <ul className="ps-0 mb-0 list-unstyled">
                        <li className="contact_li_bg py-3 w-100">
                          <SearchBar
                            searchType={search.searchType}
                            searchTerm={search.searchTerm}
                            onSearchChange={handleSearch}
                            onSearchRequest={handleSearchRequest}
                            suggestions={search.suggestions}
                            onSuggestionClick={handleSuggestionClick}
                            onViewMore={search.handleViewMore}
                            lastKey={search.lastKey}
                            remainingCompanies={search.remainingCompanies}
                            isDropdownOpen={search.isDropdownOpen}
                            onDropdownToggle={() =>
                              search.setIsDropdownOpen(!search.isDropdownOpen)
                            }
                            onDropdownChange={search.handleDropdownChange}
                            dropdownVisible={true}
                            showDropdown={true}
                            isSmallScreen={false}
                            ref={searchRef}
                            dropdownRef={dropdownRef}
                          />
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Large Screen Navbar */}
          <div className="collapse navbar-collapse" id="navbarContent">
            <SearchBar
              searchType={search.searchType}
              searchTerm={search.searchTerm}
              onSearchChange={handleSearch}
              onSearchRequest={handleSearchRequest}
              suggestions={search.suggestions}
              onSuggestionClick={handleSuggestionClick}
              onViewMore={search.handleViewMore}
              lastKey={search.lastKey}
              remainingCompanies={search.remainingCompanies}
              isDropdownOpen={search.isDropdownOpen}
              onDropdownToggle={() =>
                search.setIsDropdownOpen(!search.isDropdownOpen)
              }
              onDropdownChange={search.handleDropdownChange}
              dropdownVisible={true}
              showDropdown={true}
              isSmallScreen={false}
              className="d-none d-lg-flex mx-auto searchbar_sm_screen"
              ref={searchRef}
            />

            <div className="d-flex align-items-center gap-2">
              {auth.isLoggedIn ? (
                <ProfileDropdown
                  profileDropdownOpen={auth.profileDropdownOpen}
                  setProfileDropdownOpen={auth.setProfileDropdownOpen}
                  onSignOut={auth.handleSignOut}
                  ref={profileDropdownRef}
                />
              ) : (
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-custom sign-up"
                    onClick={() => dispatch(openSignUp())}
                    style={{ cursor: "pointer" }}
                  >
                    Sign Up
                  </button>
                  <button
                    className="btn btn-custom sign-in"
                    onClick={() => dispatch(openSignIn())}
                    style={{ cursor: "pointer" }}
                  >
                    Sign In
                  </button>
                </div>
              )}
            </div>

            {/* Buy Me a Coffee (Large Screen) */}
            <a
              href="https://buymeacoffee.com/ratemymanager"
              target="_blank"
              rel="noopener noreferrer"
              className="ms-2 d-none d-lg-block"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <img
                src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                alt="Buy Me a Coffee"
                style={{ height: "28px", width: "auto" }}
              />
            </a>

            {/* Bootstrap Modal */}
            <Modal
              show={mobile.show}
              onHide={mobile.handleClose}
              centered
              dialogClassName="custom-modal"
            >
              <Modal.Body className="custom-modal-body">
                <div className="modal-content-wrapper">
                  <h2 className="modal-title">
                    ✨ Something New is Coming! ✨
                  </h2>
                  <p className="modal-text">
                    🚀 We're working on an exciting new feature. Stay tuned!
                  </p>
                  <Button
                    variant="light"
                    className="close-button"
                    onClick={mobile.handleClose}
                  >
                    Got It!
                  </Button>
                </div>
              </Modal.Body>
            </Modal>
            {/* Authentication Modals */}
            <AuthModals
              showSignIn={auth.showSignIn}
              showSignUp={auth.showSignUp}
              showSuccessModal={auth.showSuccessModal}
              setShowSuccessModal={auth.setShowSuccessModal}
              showModal={auth.showModal}
              setShowModal={auth.setShowModal}
              message={auth.message}
              showPassword={auth.showPassword}
              setShowPassword={auth.setShowPassword}
              showPasswordSignUp={auth.showPasswordSignUp}
              setShowPasswordSignUp={auth.setShowPasswordSignUp}
              submitError={auth.submitError}
              onSignIn={auth.handleSignIn}
              onSignUp={auth.handleSignUp}
              passwordResetModal={auth.passwordResetModal}
              setPasswordResetModal={auth.setPasswordResetModal}
              updatePasswordModal={auth.updatePasswordModal}
              setUpdatePasswordModal={auth.setUpdatePasswordModal}
            />
          </div>
        </div>
      </nav>

      {/* Link Navbar */}
      <LinkNavbar />

      <div>
        {/* Hero Section */}
        {location.pathname === "/" && (
          <div className="hero_bg px-lg-2 px-md-2 px-2 pb-1">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <div className="pt-lg-5 pt-md-5 pt-1">
                    <div className="d-flex justify-content-center">
                      <img
                        src={heroImg}
                        className="img-fluid"
                        style={{ width: "250px" }}
                        alt="hero-img"
                      />
                    </div>
                  </div>
                  <div className="text-center">
                    <h3 className="hero_h3 pt-2">
                      shaping workplace culture, one review at a time
                    </h3>
                  </div>
                  <div className="pb-2 px-lg-0 px-md-0 px-2"></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trending Reviews Component */}
        <TrendingReviews
          reviews={reviews.reviews}
          showReviewModal={reviews.showReviewModal}
          selectedReview={reviews.selectedReview}
          engagements={reviews.engagements}
          onShowReviewModal={reviews.handleShowReviewModal}
          onCloseReviewModal={reviews.handleCloseReviewModal}
          onHandleEngagement={reviews.handleEngagement}
          location={location}
        />

        {/* All reviews page specific content */}
        {location.pathname === "/all-reviews" && (
          <div className="container px-lg-2 px-md-2 px-3">
            <div className="row">
              <h3 className="trending_reviews_page pt-lg-5 pt-md-4 pt-3">
                Trending Reviews
              </h3>
              <div className="col-lg-3 col-md-4 col-sm-6 col-12 mt-4">
                <Link to="/add-review" style={{ textDecoration: "none" }}>
                  <div className="d-flex justify-content-end pb-1">
                    <p className="mb-0">
                      <i
                        className="fas fa-clock"
                        style={{ color: "#9C9C9C" }}
                      />
                    </p>
                    <p className="ps-1 access_time_now pt-1 mb-0"> Now </p>
                  </div>
                  <div
                    style={{ textDecoration: "none", cursor: "pointer" }}
                    onClick={handleSecondSearchClick}
                  >
                    <div className="add_review_box">
                      <div className="d-flex justify-content-center flex-column align-items-center h-100">
                        <h4 className="add_review_h4">+ Add A Review</h4>
                        <p className="add_review_p mb-0 pt-2 text-center">
                          Reviews are Anonymous!
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Navbar;
